export type ProductView = {
  id: 'student' | 'teacher' | 'ranking'
  label: string
  kicker: string
  title: string
  description: string
  image: string
  alt: string
  width: number
  height: number
  imageLabel?: string
  secondaryImage?: string
  secondaryAlt?: string
  secondaryImageLabel?: string
}

export type ComparisonRow = {
  product: string
  mode: string
  environment: string
  courseControl: string
  fit: string
  source?: string
}

export type Testimonial = {
  author: string
  quote: string
}

export type PlatformFeature = {
  number: string
  category: string
  title: string
  description: string
}

export const testimonials: Testimonial[] = [
  {
    author: 'Uczestnik 1',
    quote: 'Platforma od strony technicznej sprawuje się fantastycznie. Działa płynnie, szybko się wczytuje i jest stabilna.',
  },
  {
    author: 'Uczestnik 2',
    quote: 'Moim zdaniem platforma spełnia swoje zadanie, jest przejrzysta i łatwa w nawigacji. Szybkość ładowania instancji jest świetna.',
  },
  {
    author: 'Uczestnik 3',
    quote: 'Bardzo podoba mi się świeże podejście Politechniki i praca z CTF-ami. Brakowało mi takiej platformy, gdy zaczynałem studia.',
  },
  {
    author: 'Uczestnik 4',
    quote: 'Bardzo ciekawa inicjatywa, która wykorzystuje element grywalizacji.',
  },
]

export const testimonialsEn: Testimonial[] = [
  { author: 'Participant 1', quote: 'From a technical perspective, the platform works brilliantly. It is smooth, loads quickly and remains stable.' },
  { author: 'Participant 2', quote: 'The platform does its job, is clear and easy to navigate. Lab instances start remarkably quickly.' },
  { author: 'Participant 3', quote: 'I really appreciate this fresh approach at the university and the use of CTFs. I wish this platform had existed when I started studying.' },
  { author: 'Participant 4', quote: 'A very interesting initiative that uses gamification in a meaningful way.' },
]

export const productViews: ProductView[] = [
  {
    id: 'teacher',
    label: 'Prowadzący',
    kicker: '01 / PERSPEKTYWA PROWADZĄCEGO',
    title: 'Postępy całej grupy w jednym miejscu.',
    description: 'Prowadzący układa program, przypisuje zadania i obserwuje postęp grupy. Może też otworzyć profil konkretnego uczestnika, aby przeanalizować jego KPI, poziomy trudności, pozycję w rankingu i tempo rozwiązywania zadań.',
    image: 'images/dashboard-wykladowcy.webp',
    alt: 'Dashboard prowadzącego z wynikami grup, aktywnością i statystykami zadań',
    width: 1600,
    height: 920,
    imageLabel: 'Dashboard grupy',
    secondaryImage: 'images/profil-uczestnika.webp',
    secondaryAlt: 'Profil uczestnika dostępny dla prowadzącego z indywidualnymi KPI, postępem i poziomami trudności',
    secondaryImageLabel: 'Profil uczestnika i indywidualne KPI',
  },
  {
    id: 'student',
    label: 'Student',
    kicker: '02 / PERSPEKTYWA STUDENTA',
    title: 'Postęp ma kontekst i tempo.',
    description: 'Student widzi bieżące zadania, własną instancję, punkty i rozwój w czasie. Streak, first blood i poziomy trudności wspierają regularną pracę bez zamiany zajęć w automat do odznak.',
    image: 'images/dashboard-studenta.webp',
    alt: 'Dashboard studenta z punktami, seriami, odznakami i trendem rankingowym',
    width: 1600,
    height: 920,
  },
  {
    id: 'ranking',
    label: 'Grupa',
    kicker: '03 / PRACA W CZASIE',
    title: 'Ranking pokazuje historię, nie tylko podium.',
    description: 'Widok grupy łączy bieżącą pozycję z trendem. Prowadzący może odróżnić jednorazowy skok od systematycznej pracy i rozmawiać o realnym postępie.',
    image: 'images/ranking-grupy.webp',
    alt: 'Ranking grupy z podium, wykresem trendu i tabelą punktów',
    width: 1600,
    height: 920,
  },
]

export const productViewsEn: ProductView[] = [
  {
    id: 'teacher', label: 'Lecturer', kicker: '01 / LECTURER VIEW', title: "Track the whole group's progress in one place.",
    description: 'Lecturers design the curriculum, assign labs and monitor group progress. They can also open an individual participant profile to analyse KPIs, difficulty levels, ranking position and problem-solving pace.',
    image: 'images/dashboard-wykladowcy-en.webp', alt: 'Lecturer dashboard with group results, activity and lab statistics', width: 1600, height: 920,
    imageLabel: 'Group dashboard', secondaryImage: 'images/profil-uczestnika-en.webp', secondaryAlt: 'Participant profile available to the lecturer with individual KPIs, progress and difficulty levels', secondaryImageLabel: 'Participant profile and individual KPIs',
  },
  {
    id: 'student', label: 'Student', kicker: '02 / STUDENT VIEW', title: 'Progress has context and momentum.',
    description: 'Students see current labs, their own instance, points and progress over time. Streaks, first blood and difficulty levels encourage regular practice without turning the course into a badge dispenser.',
    image: 'images/dashboard-studenta-en.webp', alt: 'Student dashboard with points, streaks, badges and ranking trend', width: 1600, height: 920,
  },
  {
    id: 'ranking', label: 'Group', kicker: '03 / PROGRESS OVER TIME', title: 'The ranking shows history, not just the podium.',
    description: 'The group view combines current position with a trend. Lecturers can distinguish a one-off spike from consistent work and discuss genuine progress.',
    image: 'images/ranking-grupy-en.webp', alt: 'Group ranking with podium, trend chart and score table', width: 1600, height: 920,
  },
]

export const platformFeatures: PlatformFeature[] = [
  {
    number: '01',
    category: 'Planowanie',
    title: 'Grupy, role i harmonogram',
    description: 'Twórz grupy, przypisuj role i określaj terminy publikacji zadań. Program zajęć, uczestnicy i wyniki pozostają połączone w jednym widoku.',
  },
  {
    number: '02',
    category: 'Program',
    title: 'Scenariusze gotowe i własne',
    description: 'Korzystaj ze stale rozwijanego katalogu laboratoriów lub dodawaj własne zadania. Każdy scenariusz możesz przypisać do wybranej grupy i udostępnić w określonym czasie.',
  },
  {
    number: '03',
    category: 'Izolacja',
    title: 'Osobna instancja dla uczestnika',
    description: 'PentestCamp uruchamia odizolowane środowisko, generuje dynamiczną flagę i przekazuje uczestnikowi wyłącznie unikalny URL jego zadania.',
  },
  {
    number: '04',
    category: 'Monitoring',
    title: 'Podgląd zajęć na żywo',
    description: 'Strumień zdarzeń pokazuje zdobyte flagi, pierwsze rozwiązania i przyznane odznaki. Prowadzący może reagować jeszcze w trakcie laboratorium.',
  },
  {
    number: '05',
    category: 'Analityka',
    title: 'Profil uczestnika i indywidualne KPI',
    description: 'Punkty, tempo pracy, poziomy trudności, pozycja w rankingu i historia aktywności pomagają ocenić postęp oraz wskazać miejsce wymagające wsparcia.',
  },
  {
    number: '06',
    category: 'Zaangażowanie',
    title: 'Regularność widoczna w wynikach',
    description: 'Serie aktywności, odznaki, ranking i wyróżnienie pierwszego rozwiązania wspierają systematyczną pracę, nie zastępując celu dydaktycznego zajęć.',
  },
]

export const platformFeaturesEn: PlatformFeature[] = [
  {
    number: '01',
    category: 'Planning',
    title: 'Groups, roles and schedules',
    description: 'Create groups, assign roles and set lab publication dates. The curriculum, participants and results remain connected in one view.',
  },
  {
    number: '02',
    category: 'Curriculum',
    title: 'Platform and custom scenarios',
    description: 'Use the growing lab catalogue or add your own exercises. Assign each scenario to a selected group and decide when it becomes available.',
  },
  {
    number: '03',
    category: 'Isolation',
    title: 'A separate instance for each participant',
    description: 'PentestCamp starts an isolated environment, generates a dynamic flag and gives each participant only the unique URL of their lab.',
  },
  {
    number: '04',
    category: 'Monitoring',
    title: 'A live view of the class',
    description: 'The event feed shows captured flags, first solutions and awarded badges. Lecturers can respond while the lab is still in progress.',
  },
  {
    number: '05',
    category: 'Analytics',
    title: 'Participant profiles and individual KPIs',
    description: 'Points, pace, difficulty levels, ranking position and activity history help lecturers assess progress and identify where support is needed.',
  },
  {
    number: '06',
    category: 'Engagement',
    title: 'Regular practice reflected in results',
    description: 'Activity streaks, badges, rankings and first-solver recognition encourage consistent practice without replacing the learning objective.',
  },
]

export const comparisonRows: ComparisonRow[] = [
  {
    product: 'PentestCamp',
    mode: 'semester-first',
    environment: 'Stale rozwijany katalog laboratoriów aplikacyjnych; nowe scenariusze są regularnie dodawane, a każdy uczestnik pracuje na izolowanej instancji',
    courseControl: 'Program, własne zadania, grupy i terminy po stronie prowadzącego',
    fit: 'Regularne zajęcia praktyczne, zaliczenia i rozwój grupy',
  },
  {
    product: 'CTFd',
    mode: 'event-first',
    environment: 'Platforma CTF z hostingiem i wdrażaniem challenge’y',
    courseControl: 'Własne zadania, scoring i format wydarzenia',
    fit: 'Warsztaty, konkursy i wydarzenia CTF',
    source: 'https://ctfd.io/',
  },
  {
    product: 'HTB / TryHackMe',
    mode: 'catalog-first',
    environment: 'Duże biblioteki gotowych laboratoriów i ścieżek',
    courseControl: 'Dobór z katalogu dostawcy, ścieżki i dashboard zespołu',
    fit: 'Samodzielny rozwój kompetencji i szkolenie zespołów',
    source: 'https://www.hackthebox.com/business',
  },
  {
    product: 'CDeX',
    mode: 'simulation-first',
    environment: 'Pełne sieci IT/OT, digital twins i ćwiczenia red/blue team',
    courseControl: 'Scenariusze cyber range i odwzorowanie infrastruktury',
    fit: 'Gotowość operacyjna, złożone symulacje i ćwiczenia organizacji',
    source: 'https://cdex.cloud/',
  },
]

export const comparisonRowsEn: ComparisonRow[] = [
  { product: 'PentestCamp', mode: 'semester-first', environment: 'An evolving catalogue of application labs with new scenarios added regularly and an isolated instance for every participant', courseControl: 'Curriculum, custom labs, groups and deadlines controlled by the lecturer', fit: 'Recurring practical classes, assessment and group development' },
  { product: 'CTFd', mode: 'event-first', environment: 'CTF platform with challenge hosting and deployment', courseControl: 'Custom challenges, scoring and event format', fit: 'Workshops, competitions and CTF events', source: 'https://ctfd.io/' },
  { product: 'HTB / TryHackMe', mode: 'catalog-first', environment: 'Large libraries of ready-made labs and learning paths', courseControl: 'Selection from the provider catalogue, paths and team dashboards', fit: 'Self-directed skills development and team training', source: 'https://www.hackthebox.com/business' },
  { product: 'CDeX', mode: 'simulation-first', environment: 'Complete IT/OT networks, digital twins and red/blue team exercises', courseControl: 'Cyber-range scenarios and infrastructure replication', fit: 'Operational readiness, complex simulations and organisation-wide exercises', source: 'https://cdex.cloud/' },
]
